import {
  Box, Card, CardContent, CardActionArea, TextField, Typography, Button,
  IconButton, Divider, MenuItem, Select, FormControl, Chip,
  InputAdornment, Dialog, DialogTitle, DialogContent, DialogActions, Alert,
  Badge, List, ListItem, ListItemText, Tooltip, Avatar, CircularProgress,
  Paper,
} from '@mui/material';
import {
  Add, Remove, Delete, QrCodeScanner, Search, ShoppingCart, Check, WhatsApp,
  Print, AddShoppingCart, Pause, PlayArrow, AttachMoney, AddBox, PersonAdd,
  Edit, Today, Close, Notes, LocalOffer, PictureAsPdf,
  Category as CategoryIcon, PointOfSale, History,
} from '@mui/icons-material';
import { useState, useRef, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '../../api/axios';
import { showSnackbar } from '../../features/ui/uiSlice';
import InvoicePrint from './InvoicePrint';
import { useReactToPrint } from 'react-to-print';

// ─── Constants ───────────────────────────────────────────────────────────────
const PMETHODS = [
  { value: 'cash',          label: 'Cash',          color: '#16a34a', bg: '#f0fdf4' },
  { value: 'card',          label: 'Card',           color: '#2563eb', bg: '#eff6ff' },
  { value: 'bank_transfer', label: 'Bank',           color: '#7c3aed', bg: '#f5f3ff' },
  { value: 'jazzcash',      label: 'JazzCash',       color: '#dc2626', bg: '#fef2f2' },
  { value: 'easypaisa',     label: 'EasyPaisa',      color: '#15803d', bg: '#f0fdf4' },
  { value: 'credit',        label: 'Khata (Credit)', color: '#d97706', bg: '#fffbeb' },
];

const PRODUCT_COLORS = [
  '#6366f1','#0ea5e9','#10b981','#f59e0b','#ef4444',
  '#8b5cf6','#06b6d4','#ec4899','#84cc16','#f97316',
];

const PPP = 20; // products per page

// ─── Helpers ─────────────────────────────────────────────────────────────────
const fmt  = n => Math.round(n || 0).toLocaleString('en-PK');
const productColor = name => PRODUCT_COLORS[(name?.charCodeAt(0) || 0) % PRODUCT_COLORS.length];
const isExpiringSoon = date => {
  if (!date) return false;
  const days = Math.floor((new Date(date) - new Date()) / 86400000);
  return days >= 0 && days <= 30; // only upcoming expiry, not already expired
};
const isExpired = date => date && new Date(date) < new Date();

// Parse variant label from either schema:
// Schema A (variants.routes): attribute_name + attribute_value
// Schema B (products.service): attributes JSON
const variantLabel = v => {
  if (v.attribute_name && v.attribute_value) return `${v.attribute_name}: ${v.attribute_value}`;
  if (v.attributes) {
    try {
      const attrs = typeof v.attributes === 'string' ? JSON.parse(v.attributes) : v.attributes;
      return Object.entries(attrs).map(([k, val]) => `${k}: ${val}`).join(' / ');
    } catch { return v.sku || 'Variant'; }
  }
  return v.sku || 'Variant';
};
const variantPrice = v => +(v.sale_price ?? v.price ?? 0);
const variantStock = v => +(v.stock ?? 0);

// ─── Sub-component: ProductCard ───────────────────────────────────────────────
function ProductCard({ product, onAdd }) {
  const outOfStock = +product.stock <= 0;
  const lowStock   = !outOfStock && +product.stock <= +(product.low_stock_alert || 5);
  const color      = productColor(product.name);

  return (
    <Card elevation={0}
      sx={{
        border: '1px solid',
        borderColor: outOfStock ? 'error.light' : 'divider',
        borderRadius: 2,
        overflow: 'hidden',
        opacity: outOfStock ? 0.55 : 1,
        transition: 'all .15s',
        '&:hover': outOfStock ? {} : { borderColor: 'primary.main', transform: 'translateY(-2px)', boxShadow: 3 },
      }}
    >
      <CardActionArea onClick={() => !outOfStock && onAdd(product)} disabled={outOfStock}>
        {/* Image / color header */}
        <Box sx={{ height: 72, bgcolor: color, display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {product.image_url
            ? <Box component="img" src={product.image_url} alt={product.name}
                sx={{ width: '100%', height: '100%', objectFit: 'cover', position: 'absolute', inset: 0 }} />
            : <Typography sx={{ fontSize: 28, fontWeight: 700, color: 'rgba(255,255,255,0.85)' }}>
                {product.name?.[0]?.toUpperCase()}
              </Typography>
          }
          {product.is_medicine && (
            <Chip label="Rx" size="small"
              sx={{ position: 'absolute', top: 4, right: 4, bgcolor: 'white', color: '#dc2626', fontWeight: 700, height: 18, fontSize: 10 }} />
          )}
          {product.type === 'variable' && (
            <Chip label="Variants" size="small"
              sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'rgba(255,255,255,0.9)', color: color, fontWeight: 700, height: 18, fontSize: 10 }} />
          )}
        </Box>

        <CardContent sx={{ p: 1, pb: '8px !important' }}>
          <Typography variant="body2" fontWeight={600} noWrap title={product.name}>{product.name}</Typography>
          {product.sku && <Typography variant="caption" color="text.disabled" display="block" noWrap>{product.sku}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="body2" fontWeight={700} color="primary.main">₨{fmt(product.sale_price)}</Typography>
            <Chip
              label={outOfStock ? 'OUT' : lowStock ? `Low:${product.stock}` : product.stock}
              size="small"
              color={outOfStock ? 'error' : lowStock ? 'warning' : 'success'}
              sx={{ height: 16, fontSize: 10, fontWeight: 700 }}
            />
          </Box>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

// ─── Sub-component: CartRow ───────────────────────────────────────────────────
function CartRow({ item, idx, onQty, onQtyDirect, onRemove, onEditPrice, onEditDisc, onNote,
                   editingPriceIdx, editingPriceVal, setEditingPriceVal, commitPrice,
                   editingDiscIdx, editingDiscVal, setEditingDiscVal, commitDiscount }) {
  const lineTotal = item.unit_price * item.quantity - (item.discount_amount || 0);
  const color     = productColor(item.product_name);
  const expired   = isExpired(item.expiry_date);
  const expiring  = isExpiringSoon(item.expiry_date);

  return (
    <Box sx={{ px: 1.5, py: 1, borderBottom: '1px solid', borderColor: 'divider', '&:hover': { bgcolor: 'action.hover' } }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        {/* Avatar */}
        <Avatar sx={{ width: 36, height: 36, bgcolor: color, fontSize: 14, fontWeight: 700, flexShrink: 0, mt: 0.25 }}>
          {item.product_name?.[0]?.toUpperCase()}
        </Avatar>

        {/* Details */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Typography variant="body2" fontWeight={600} noWrap sx={{ flex: 1 }}>{item.product_name}</Typography>
            {item.product_id === null && <Chip label="custom" size="small" variant="outlined" sx={{ height: 14, fontSize: 9 }} />}
            {item.variant_label && <Chip label={item.variant_label} size="small" color="primary" variant="outlined" sx={{ height: 14, fontSize: 9 }} />}
          </Box>

          {/* Medical warnings */}
          {expired && <Alert severity="error" sx={{ py: 0, px: 1, mb: 0.5, fontSize: 11 }}>⚠ EXPIRED: {item.expiry_date}</Alert>}
          {!expired && expiring && <Alert severity="warning" sx={{ py: 0, px: 1, mb: 0.5, fontSize: 11 }}>Expires: {item.expiry_date}</Alert>}
          {item.notes && <Typography variant="caption" color="text.secondary" fontStyle="italic">Note: {item.notes}</Typography>}

          {/* Controls row */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
            {/* Qty */}
            <Box sx={{ display: 'flex', alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
              <IconButton size="small" onClick={() => onQty(idx, -1)} sx={{ p: 0.25 }}><Remove sx={{ fontSize: 13 }} /></IconButton>
              <TextField
                value={item.quantity}
                onChange={e => onQtyDirect(idx, e.target.value)}
                type="number"
                inputProps={{ min: 0.001, step: 0.01, style: { textAlign: 'center', padding: '2px 0', fontSize: 13, width: 38 } }}
                variant="standard"
                InputProps={{ disableUnderline: true }}
              />
              <IconButton size="small" onClick={() => onQty(idx, 1)} sx={{ p: 0.25 }}><Add sx={{ fontSize: 13 }} /></IconButton>
            </Box>

            {/* Price */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
              <Typography variant="caption" color="text.secondary">×</Typography>
              {editingPriceIdx === idx ? (
                <TextField autoFocus size="small" type="number" value={editingPriceVal}
                  onChange={e => setEditingPriceVal(e.target.value)}
                  onBlur={() => commitPrice(idx)}
                  onKeyDown={e => { if (e.key === 'Enter') commitPrice(idx); if (e.key === 'Escape') {} }}
                  inputProps={{ min: 0, step: 0.01, style: { textAlign: 'right', padding: '2px 4px', fontSize: 12, width: 64 } }}
                  variant="standard" />
              ) : (
                <Tooltip title="Click to edit price">
                  <Box sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.25 }}
                    onClick={() => onEditPrice(idx, item.unit_price)}>
                    <Typography variant="body2" fontWeight={600} color="text.primary">₨{fmt(item.unit_price)}</Typography>
                    <Edit sx={{ fontSize: 10, color: 'text.disabled' }} />
                  </Box>
                </Tooltip>
              )}
            </Box>

            {/* Discount */}
            {editingDiscIdx === idx ? (
              <TextField autoFocus size="small" type="number" value={editingDiscVal}
                onChange={e => setEditingDiscVal(e.target.value)}
                onBlur={() => commitDiscount(idx)}
                onKeyDown={e => { if (e.key === 'Enter') commitDiscount(idx); }}
                placeholder="Disc ₨"
                inputProps={{ min: 0, step: 1, style: { padding: '2px 4px', fontSize: 12, width: 60 } }}
                variant="standard" />
            ) : (
              <Tooltip title="Add item discount">
                <Chip
                  label={item.discount_amount > 0 ? `-₨${fmt(item.discount_amount)}` : 'Disc'}
                  size="small"
                  variant={item.discount_amount > 0 ? 'filled' : 'outlined'}
                  color={item.discount_amount > 0 ? 'error' : 'default'}
                  onClick={() => onEditDisc(idx, item.discount_amount)}
                  sx={{ height: 18, fontSize: 10, cursor: 'pointer' }}
                />
              </Tooltip>
            )}

            {/* Note */}
            <Tooltip title={item.notes ? 'Edit note' : 'Add note'}>
              <IconButton size="small" onClick={() => onNote(idx)} sx={{ p: 0.25 }}>
                <Notes sx={{ fontSize: 13, color: item.notes ? 'primary.main' : 'text.disabled' }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* Right: total + delete */}
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Typography variant="body2" fontWeight={700} color={lineTotal < 0 ? 'error.main' : 'text.primary'}>
            ₨{fmt(lineTotal)}
          </Typography>
          <IconButton size="small" color="error" onClick={() => onRemove(idx)} sx={{ p: 0.25, mt: 0.5 }}>
            <Delete sx={{ fontSize: 14 }} />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
}

// ─── Main POSPage ─────────────────────────────────────────────────────────────
export default function POSPage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(s => s.auth);
  const printRef = useRef();

  // ── Product browsing ────────────────────────────────────────────────────────
  const [categories, setCategories]         = useState([]);
  const [selCategory, setSelCategory]       = useState('all');
  const [products, setProducts]             = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchTerm, setSearchTerm]         = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [productPage, setProductPage]       = useState(1);
  const [productTotal, setProductTotal]     = useState(0);

  // ── Cart ────────────────────────────────────────────────────────────────────
  const [cart, setCart]                 = useState([]);
  const [editingPriceIdx, setEditingPriceIdx] = useState(null);
  const [editingPriceVal, setEditingPriceVal] = useState('');
  const [editingDiscIdx, setEditingDiscIdx]   = useState(null);
  const [editingDiscVal, setEditingDiscVal]   = useState('');
  const [noteIdx, setNoteIdx]           = useState(null);
  const [noteVal, setNoteVal]           = useState('');

  // ── Customer ────────────────────────────────────────────────────────────────
  const [customer, setCustomer]             = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustResults, setShowCustResults] = useState(false);

  // ── Order ───────────────────────────────────────────────────────────────────
  const [orderDiscType, setOrderDiscType] = useState('fixed');
  const [orderDisc, setOrderDisc]         = useState(0);
  const [orderTax, setOrderTax]           = useState(0);
  const [saleNotes, setSaleNotes]         = useState('');
  const [redeemPoints, setRedeemPoints]   = useState(0);
  const [voucherCode, setVoucherCode]     = useState('');
  const [voucherDisc, setVoucherDisc]     = useState(0);
  const [voucherId, setVoucherId]         = useState(null);
  const [voucherError, setVoucherError]   = useState('');

  // ── Payments (multi-method) ─────────────────────────────────────────────────
  const [payments, setPayments] = useState([{ method: 'cash', amount: '' }]);

  // ── Dialogs ─────────────────────────────────────────────────────────────────
  const [variantDialog, setVariantDialog] = useState(null);   // { product, variants }
  const [customDialog, setCustomDialog]   = useState(false);
  const [customName, setCustomName]       = useState('');
  const [customPrice, setCustomPrice]     = useState('');
  const [customQty, setCustomQty]         = useState('1');
  const [newCustDialog, setNewCustDialog] = useState(false);
  const [newCustName, setNewCustName]     = useState('');
  const [newCustPhone, setNewCustPhone]   = useState('');
  const [newCustLoading, setNewCustLoading] = useState(false);
  const [heldDialog, setHeldDialog]       = useState(false);
  const [expDialog, setExpDialog]         = useState(false);
  const [expAmount, setExpAmount]         = useState('');
  const [expCat, setExpCat]               = useState('');
  const [expNote, setExpNote]             = useState('');
  const [expCats, setExpCats]             = useState([]);
  const [expLoading, setExpLoading]       = useState(false);

  // ── Misc ────────────────────────────────────────────────────────────────────
  const [completedSale, setCompletedSale] = useState(null);
  const [loading, setLoading]             = useState(false);
  const [tenant, setTenant]               = useState(null);
  const [invoiceFormat, setInvoiceFormat] = useState(localStorage.getItem('pos_invoice_format') || 'thermal80');
  const [heldSales, setHeldSales]         = useState(() => {
    try { return JSON.parse(localStorage.getItem('pos_held_sales') || '[]'); } catch { return []; }
  });
  const [variantLoading, setVariantLoading] = useState(false);

  // ─── Fetch on mount ──────────────────────────────────────────────────────────
  useEffect(() => {
    api.get('/settings/business').then(r => setTenant(r.data.data)).catch(() => {});
    api.get('/expenses/categories').then(r => setExpCats(r.data.data || [])).catch(() => {});
    api.get('/categories?limit=50').then(r => setCategories(r.data.data || [])).catch(() => {});
  }, []);

  // ─── Debounce search ─────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchTerm); setProductPage(1); }, 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // ─── Fetch products ───────────────────────────────────────────────────────────
  useEffect(() => {
    setProductsLoading(true);
    const params = new URLSearchParams({ page: productPage, limit: PPP });
    if (debouncedSearch) params.set('search', debouncedSearch);
    if (selCategory !== 'all') params.set('category_id', selCategory);
    api.get(`/products?${params}`)
      .then(r => {
        setProducts(r.data.data || []);
        setProductTotal(r.data.pagination?.total || 0);
      })
      .catch(() => {})
      .finally(() => setProductsLoading(false));
  }, [debouncedSearch, selCategory, productPage]);

  // ─── Calculations ─────────────────────────────────────────────────────────────
  const subtotal       = cart.reduce((s, i) => s + i.unit_price * i.quantity - (i.discount_amount || 0), 0);
  const maxRedeemable  = customer ? Math.min(customer.loyalty_points || 0, Math.floor(subtotal * 0.2)) : 0;
  const itemDiscsTotal = cart.reduce((s, i) => s + (i.discount_amount || 0), 0);
  const orderDiscAmt   = orderDiscType === 'percent' ? subtotal * (+orderDisc / 100) : +orderDisc;
  const pointsDiscAmt  = Math.min(+redeemPoints || 0, maxRedeemable);
  const afterDisc      = Math.max(0, subtotal - orderDiscAmt - pointsDiscAmt - voucherDisc);
  const taxAmt         = afterDisc * (+orderTax / 100);
  const total          = afterDisc + taxAmt;
  const totalPaid      = payments.reduce((s, p) => s + (+p.amount || 0), 0);
  const change         = Math.max(0, totalPaid - total);
  const due            = Math.max(0, total - totalPaid);

  const handlePrint = useReactToPrint({ contentRef: printRef });

  // ─── Product click → add or show variants ────────────────────────────────────
  const handleProductClick = useCallback(async (product) => {
    if (product.type === 'variable') {
      setVariantLoading(true);
      setVariantDialog(null);
      try {
        const { data } = await api.get(`/products/${product.id}`);
        setVariantDialog({ product, variants: data.data.variants || [] });
      } catch {
        dispatch(showSnackbar({ message: 'Could not load variants', severity: 'error' }));
      } finally { setVariantLoading(false); }
    } else {
      setCart(prev => {
        const existKey = prev.findIndex(i => i.product_id === product.id && !i.variant_id);
        if (existKey >= 0) return prev.map((c, i) => i === existKey ? { ...c, quantity: +(c.quantity + 1).toFixed(3) } : c);
        return [...prev, {
          product_id: product.id, product_name: product.name, sku: product.sku,
          unit_price: product.sale_price, quantity: 1, discount_amount: 0,
          stock: product.stock, is_medicine: product.is_medicine,
          variant_id: null, variant_label: null,
        }];
      });
      dispatch(showSnackbar({ message: `${product.name} added`, severity: 'success' }));
    }
  }, [dispatch]);

  const addFromVariant = (selectedVariant) => {
    const { product } = variantDialog;
    const v = selectedVariant;
    const label = variantLabel(v);
    const price  = variantPrice(v) || product.sale_price;
    const stock  = variantStock(v);
    setCart(prev => {
      const existKey = prev.findIndex(i => i.product_id === product.id && i.variant_id === v.id);
      if (existKey >= 0) return prev.map((c, i) => i === existKey ? { ...c, quantity: +(c.quantity + 1).toFixed(3) } : c);
      return [...prev, {
        product_id: product.id, product_name: product.name,
        sku: v.sku || product.sku,
        unit_price: price, quantity: 1, discount_amount: 0,
        stock, variant_id: v.id, variant_label: label,
        is_medicine: product.is_medicine,
      }];
    });
    dispatch(showSnackbar({ message: `${product.name} (${label}) added`, severity: 'success' }));
    setVariantDialog(null);
  };

  // addToCartItem — used for custom items only (barcode scan and product grid use inline setCart)
  const addToCartItem = (item) => {
    setCart(prev => {
      const existKey = item.variant_id
        ? prev.findIndex(i => i.product_id === item.product_id && i.variant_id === item.variant_id)
        : prev.findIndex(i => i.product_id === item.product_id && item.product_id !== null && !i.variant_id);
      if (existKey >= 0) {
        return prev.map((c, i) => i === existKey ? { ...c, quantity: +(c.quantity + 1).toFixed(3) } : c);
      }
      return [...prev, item];
    });
    dispatch(showSnackbar({ message: `${item.product_name} added`, severity: 'success' }));
  };

  // ─── Barcode scan ──────────────────────────────────────────────────────────
  const scanBarcode = async (barcode) => {
    try {
      const { data } = await api.get(`/products/barcode/${barcode}`);
      handleProductClick(data.data);
      setSearchTerm('');
    } catch { dispatch(showSnackbar({ message: 'Product not found', severity: 'error' })); }
  };

  // ─── Cart helpers ──────────────────────────────────────────────────────────
  const updateQty = (idx, delta) => setCart(prev =>
    prev.map((item, i) => {
      if (i !== idx) return item;
      const q = +(item.quantity + delta).toFixed(3);
      return q <= 0 ? null : { ...item, quantity: q };
    }).filter(Boolean)
  );

  const setQtyDirect = (idx, val) => {
    const q = parseFloat(val);
    if (!isNaN(q) && q >= 0) setCart(prev => prev.map((item, i) => i === idx ? { ...item, quantity: q } : item));
  };

  const removeItem = idx => setCart(prev => prev.filter((_, i) => i !== idx));

  const commitPrice = (idx) => {
    const v = parseFloat(editingPriceVal);
    if (!isNaN(v) && v >= 0) setCart(prev => prev.map((item, i) => i === idx ? { ...item, unit_price: v } : item));
    setEditingPriceIdx(null);
  };

  const commitDiscount = (idx) => {
    const v = parseFloat(editingDiscVal);
    if (!isNaN(v) && v >= 0) {
      setCart(prev => prev.map((item, i) => {
        if (i !== idx) return item;
        return { ...item, discount_amount: Math.min(v, item.unit_price * item.quantity) };
      }));
    }
    setEditingDiscIdx(null);
  };

  const saveNote = () => {
    setCart(prev => prev.map((item, i) => i === noteIdx ? { ...item, notes: noteVal } : item));
    setNoteIdx(null);
  };

  // ─── Customer search ───────────────────────────────────────────────────────
  const searchCustomers = useCallback(async (term) => {
    if (term.length < 2) { setCustomerResults([]); setShowCustResults(false); return; }
    try {
      const { data } = await api.get(`/customers?search=${encodeURIComponent(term)}&limit=6`);
      setCustomerResults(data.data || []);
      setShowCustResults(true);
    } catch { /* silent — don't break on network error */ }
  }, []);

  const createCustomer = async () => {
    if (!newCustName.trim()) return;
    setNewCustLoading(true);
    try {
      const { data } = await api.post('/customers', { name: newCustName.trim(), phone: newCustPhone.trim() || undefined });
      const nc = { id: data.data.id, name: newCustName.trim(), phone: newCustPhone.trim(), loyalty_points: 0, credit_balance: 0 };
      setCustomer(nc); setCustomerSearch(newCustName.trim());
      setShowCustResults(false); setNewCustDialog(false);
      setNewCustName(''); setNewCustPhone('');
      dispatch(showSnackbar({ message: 'Customer added & selected', severity: 'success' }));
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Failed', severity: 'error' }));
    } finally { setNewCustLoading(false); }
  };

  // ─── Custom item ───────────────────────────────────────────────────────────
  const addCustomItem = () => {
    if (!customName.trim() || !customPrice) return;
    addToCartItem({ product_id: null, product_name: customName.trim(), sku: null,
      unit_price: +customPrice, quantity: +customQty || 1, discount_amount: 0, stock: 9999 });
    setCustomDialog(false); setCustomName(''); setCustomPrice(''); setCustomQty('1');
  };

  // ─── Voucher ───────────────────────────────────────────────────────────────
  const applyVoucher = async () => {
    setVoucherError('');
    try {
      const { data } = await api.post('/vouchers/validate', { code: voucherCode, subtotal });
      setVoucherDisc(data.data.discount); setVoucherId(data.data.voucher_id);
      dispatch(showSnackbar({ message: `Voucher applied! −₨${data.data.discount}`, severity: 'success' }));
    } catch (e) {
      setVoucherError(e.response?.data?.message || 'Invalid voucher');
      setVoucherDisc(0); setVoucherId(null);
    }
  };

  // ─── Hold / Retrieve ───────────────────────────────────────────────────────
  const holdSale = () => {
    if (!cart.length) return;
    const note = window.prompt('Hold note (optional):') ?? '';
    const held = { id: Date.now(), cart, customer, orderDiscType, orderDisc, orderTax, notes: note, heldAt: new Date().toISOString() };
    const updated = [...heldSales, held];
    setHeldSales(updated);
    localStorage.setItem('pos_held_sales', JSON.stringify(updated));
    clearAll();
    dispatch(showSnackbar({ message: 'Sale held', severity: 'info' }));
  };

  const retrieveHeld = (held) => {
    setCart(held.cart); setCustomer(held.customer);
    setOrderDiscType(held.orderDiscType); setOrderDisc(held.orderDisc); setOrderTax(held.orderTax);
    const updated = heldSales.filter(h => h.id !== held.id);
    setHeldSales(updated);
    localStorage.setItem('pos_held_sales', JSON.stringify(updated));
    setHeldDialog(false);
  };

  const deleteHeld = (id) => {
    const updated = heldSales.filter(h => h.id !== id);
    setHeldSales(updated); localStorage.setItem('pos_held_sales', JSON.stringify(updated));
  };

  const clearAll = () => {
    setCart([]); setCustomer(null); setCustomerSearch(''); setCustomerResults([]);
    setOrderDisc(0); setSaleNotes(''); setPayments([{ method: 'cash', amount: '' }]);
    setRedeemPoints(0); setVoucherCode(''); setVoucherDisc(0); setVoucherId(null); setVoucherError('');
    setEditingPriceIdx(null); setEditingDiscIdx(null);
  };

  // ─── Payment rows ──────────────────────────────────────────────────────────
  const addPaymentRow   = () => setPayments(p => [...p, { method: 'card', amount: '' }]);
  const removePaymentRow = i => setPayments(p => p.filter((_, idx) => idx !== i));
  const updatePayment   = (i, field, val) => setPayments(p => p.map((row, idx) => idx === i ? { ...row, [field]: val } : row));

  // Auto-fill remaining on last payment row
  const autoFillRemaining = () => {
    const alreadyFilled = payments.slice(0, -1).reduce((s, p) => s + (+p.amount || 0), 0);
    const remaining = Math.max(0, total - alreadyFilled);
    setPayments(p => p.map((row, idx) => idx === p.length - 1 ? { ...row, amount: remaining.toFixed(0) } : row));
  };

  // ─── Complete sale ─────────────────────────────────────────────────────────
  const completeSale = async () => {
    if (!cart.length) { dispatch(showSnackbar({ message: 'Cart is empty', severity: 'warning' })); return; }

    // Check expired medicines
    const expiredItems = cart.filter(i => isExpired(i.expiry_date));
    if (expiredItems.length && !window.confirm(`⚠ ${expiredItems.length} item(s) EXPIRED. Proceed anyway?`)) return;

    // Payment validation
    if (totalPaid < total - 0.01 && !payments.some(p => p.method === 'credit')) {
      dispatch(showSnackbar({ message: 'Payment amount is less than total', severity: 'warning' })); return;
    }

    setLoading(true);
    try {
      const primaryMethod = payments[0]?.method || 'cash';
      const paymentNote = payments.length > 1
        ? payments.map(p => `${p.method}: ₨${p.amount}`).join(' + ')
        : undefined;

      const { data } = await api.post('/sales', {
        customer_id: customer?.id,
        items: cart,
        discount_type: orderDiscType,
        discount_amount: orderDiscAmt,
        tax_amount: taxAmt,
        paid_amount: totalPaid,
        payment_method: payments.length > 1 ? 'split' : primaryMethod,
        notes: [saleNotes, paymentNote].filter(Boolean).join(' | '),
        redeem_points: pointsDiscAmt > 0 ? pointsDiscAmt : undefined,
      });

      if (voucherId) api.post(`/vouchers/${voucherId}/use`).catch(() => {});

      setCompletedSale({
        ...data.data, customer, items: cart, total, taxAmt, change,
        paidAmount: totalPaid, discountAmount: orderDiscAmt,
        paymentMethod: primaryMethod, cashierName: user?.name,
      });
      clearAll();
      dispatch(showSnackbar({ message: '✓ Sale completed!', severity: 'success' }));
      setTimeout(handlePrint, 300);
    } catch (e) {
      dispatch(showSnackbar({ message: e.response?.data?.message || 'Sale failed', severity: 'error' }));
    } finally { setLoading(false); }
  };

  // ─── Quick expense ─────────────────────────────────────────────────────────
  const submitExpense = async () => {
    if (!expAmount || !expCat) return;
    setExpLoading(true);
    try {
      await api.post('/expenses', { amount: +expAmount, category_id: +expCat, title: expNote || 'POS expense', payment_method: 'cash', expense_date: new Date().toISOString().split('T')[0] });
      dispatch(showSnackbar({ message: `Expense ₨${expAmount} recorded`, severity: 'success' }));
      setExpDialog(false); setExpAmount(''); setExpCat(''); setExpNote('');
    } catch (e) { dispatch(showSnackbar({ message: 'Expense failed', severity: 'error' })); }
    finally { setExpLoading(false); }
  };

  // ─── Variant helpers ───────────────────────────────────────────────────────
  // Group variants by attribute_name for display (e.g., Color: [Red, Blue], Size: [S, M, L])
  const variantGroups = useMemo(() => {
    if (!variantDialog?.variants.length) return {};
    const groups = {};
    variantDialog.variants.forEach(v => {
      const key = v.attribute_name || 'Variant';
      if (!groups[key]) groups[key] = [];
      groups[key].push(v);
    });
    return groups;
  }, [variantDialog]);

  // ─────────────────────────────────────────────────────────────────────────────
  //  RENDER
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', overflow: 'hidden', bgcolor: 'background.default' }}>

      {/* ── TOP TOOLBAR ─────────────────────────────────────────────────────── */}
      <Paper elevation={0} square sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 1.5, py: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', bgcolor: 'background.paper' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mr: 1 }}>
          <PointOfSale color="primary" fontSize="small" />
          <Typography variant="subtitle2" fontWeight={700} color="primary">POS</Typography>
        </Box>
        <Tooltip title="Hold current sale"><span>
          <Button size="small" variant="outlined" color="warning" startIcon={<Pause />}
            onClick={holdSale} disabled={!cart.length} sx={{ minWidth: 0 }}>Hold</Button>
        </span></Tooltip>
        <Badge badgeContent={heldSales.length} color="warning">
          <Button size="small" variant="outlined" color="secondary" startIcon={<PlayArrow />}
            onClick={() => setHeldDialog(true)} disabled={!heldSales.length} sx={{ minWidth: 0 }}>Held</Button>
        </Badge>
        <Button size="small" variant="outlined" startIcon={<AddBox />}
          onClick={() => setCustomDialog(true)} sx={{ minWidth: 0 }}>Custom Item</Button>
        <Button size="small" variant="outlined" color="error" startIcon={<AttachMoney />}
          onClick={() => setExpDialog(true)} sx={{ minWidth: 0 }}>Expense</Button>
        <Button size="small" variant="outlined" startIcon={<Today />}
          onClick={() => navigate('/reports/eod')} sx={{ minWidth: 0 }}>EOD</Button>
        <Button size="small" variant="outlined" startIcon={<History />}
          onClick={() => navigate('/sales')} sx={{ minWidth: 0 }}>Sales History</Button>
        <Box sx={{ flex: 1 }} />
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select value={invoiceFormat}
            onChange={e => { setInvoiceFormat(e.target.value); localStorage.setItem('pos_invoice_format', e.target.value); }}
            variant="outlined" sx={{ fontSize: 12 }}>
            <MenuItem value="thermal80" sx={{ fontSize: 12 }}>🖨 Thermal 80mm</MenuItem>
            <MenuItem value="thermal58" sx={{ fontSize: 12 }}>🖨 Thermal 58mm</MenuItem>
            <MenuItem value="a4"        sx={{ fontSize: 12 }}>📄 A4 Invoice</MenuItem>
          </Select>
        </FormControl>
        {cart.length > 0 && (
          <Chip label={`${cart.length} item${cart.length > 1 ? 's' : ''} · ₨${fmt(total)}`}
            color="primary" size="small" />
        )}
      </Paper>

      {/* ── MAIN 3-PANEL LAYOUT ─────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden' }}>

        {/* ══ LEFT: Products ══════════════════════════════════════════════════ */}
        <Box sx={{ width: '40%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid', borderColor: 'divider' }}>

          {/* Search */}
          <Box sx={{ p: 1.5, pb: 0.75 }}>
            <TextField
              fullWidth size="small"
              placeholder="Search product, SKU, barcode…"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && searchTerm) scanBarcode(searchTerm); }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment>,
                endAdornment: <InputAdornment position="end">
                  <Tooltip title="Press Enter to scan barcode">
                    <QrCodeScanner fontSize="small" color="primary" />
                  </Tooltip>
                </InputAdornment>,
              }}
            />
          </Box>

          {/* Category tabs */}
          <Box sx={{ px: 1.5, pb: 0.5 }}>
            <Box sx={{ display: 'flex', gap: 0.75, overflowX: 'auto', pb: 0.5, '&::-webkit-scrollbar': { height: 3 } }}>
              <Chip label="All" size="small" clickable
                onClick={() => { setSelCategory('all'); setProductPage(1); }}
                color={selCategory === 'all' ? 'primary' : 'default'}
                variant={selCategory === 'all' ? 'filled' : 'outlined'}
                sx={{ flexShrink: 0 }}
              />
              {categories.map(c => (
                <Chip key={c.id} label={c.name} size="small" clickable
                  onClick={() => { setSelCategory(c.id); setProductPage(1); }}
                  color={selCategory === c.id ? 'primary' : 'default'}
                  variant={selCategory === c.id ? 'filled' : 'outlined'}
                  sx={{ flexShrink: 0 }}
                />
              ))}
            </Box>
          </Box>

          {/* Product grid — scrollable */}
          <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 1 }}>
            {productsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
            ) : products.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}>
                <CategoryIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
                <Typography variant="body2">No products found</Typography>
              </Box>
            ) : (
              <>
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(130px, 1fr))', gap: 1 }}>
                  {products.map(p => (
                    <ProductCard key={p.id} product={p} onAdd={handleProductClick} />
                  ))}
                </Box>
                {/* Pagination */}
                {productTotal > PPP && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 2 }}>
                    <Button size="small" disabled={productPage === 1} onClick={() => setProductPage(p => p - 1)}>← Prev</Button>
                    <Typography variant="caption" color="text.secondary">
                      {productPage} / {Math.ceil(productTotal / PPP)}
                    </Typography>
                    <Button size="small" disabled={productPage >= Math.ceil(productTotal / PPP)} onClick={() => setProductPage(p => p + 1)}>Next →</Button>
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {/* ══ CENTER: Cart ════════════════════════════════════════════════════ */}
        <Box sx={{ width: '35%', display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRight: '1px solid', borderColor: 'divider' }}>

          {/* Cart header */}
          <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'center', justifyContent: 'space-between', bgcolor: 'background.paper', flexShrink: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart color="primary" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700}>
                Cart {cart.length > 0 && <Chip label={cart.length} size="small" color="primary" sx={{ height: 16, fontSize: 10, ml: 0.5 }} />}
              </Typography>
            </Box>
            {cart.length > 0 && (
              <Button size="small" color="error" variant="text" onClick={clearAll} sx={{ minWidth: 0, fontSize: 11 }}>Clear All</Button>
            )}
          </Box>

          {/* Cart items — scrollable */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {cart.length === 0 ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary', gap: 1 }}>
                <ShoppingCart sx={{ fontSize: 56, opacity: 0.15 }} />
                <Typography variant="body2" color="text.disabled">Cart is empty</Typography>
                <Button size="small" startIcon={<AddBox />} onClick={() => setCustomDialog(true)}>Add Custom Item</Button>
              </Box>
            ) : (
              cart.map((item, idx) => (
                <CartRow key={idx} item={item} idx={idx}
                  onQty={updateQty} onQtyDirect={setQtyDirect} onRemove={removeItem}
                  onEditPrice={(i, v) => { setEditingPriceIdx(i); setEditingPriceVal(v); }}
                  onEditDisc={(i, v) => { setEditingDiscIdx(i); setEditingDiscVal(v || ''); }}
                  onNote={(i) => { setNoteIdx(i); setNoteVal(cart[i].notes || ''); }}
                  editingPriceIdx={editingPriceIdx} editingPriceVal={editingPriceVal}
                  setEditingPriceVal={setEditingPriceVal} commitPrice={commitPrice}
                  editingDiscIdx={editingDiscIdx} editingDiscVal={editingDiscVal}
                  setEditingDiscVal={setEditingDiscVal} commitDiscount={commitDiscount}
                />
              ))
            )}
          </Box>

          {/* Cart footer — totals */}
          <Box sx={{ borderTop: '2px solid', borderColor: 'divider', p: 1.5, bgcolor: 'background.paper', flexShrink: 0 }}>
            {/* Order discount + tax */}
            <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
              <FormControl size="small" sx={{ width: 90 }}>
                <Select value={orderDiscType} onChange={e => setOrderDiscType(e.target.value)} sx={{ fontSize: 12 }}>
                  <MenuItem value="fixed" sx={{ fontSize: 12 }}>₨ Disc</MenuItem>
                  <MenuItem value="percent" sx={{ fontSize: 12 }}>% Disc</MenuItem>
                </Select>
              </FormControl>
              <TextField size="small" type="number" label="Discount" value={orderDisc}
                onChange={e => setOrderDisc(e.target.value)} sx={{ flex: 1 }}
                inputProps={{ min: 0, step: 1 }} />
              <TextField size="small" type="number" label="GST %" value={orderTax}
                onChange={e => setOrderTax(Math.max(0, +e.target.value))}
                sx={{ width: 70 }} inputProps={{ min: 0, max: 100 }} />
            </Box>

            {/* Summary lines */}
            <Box sx={{ bgcolor: 'grey.50', borderRadius: 1.5, p: 1.25 }}>
              {[
                cart.length > 0 && subtotal !== total && ['Subtotal', `₨ ${fmt(subtotal)}`],
                itemDiscsTotal > 0 && ['Item Discounts', `− ₨ ${fmt(itemDiscsTotal)}`, '#dc2626'],
                orderDiscAmt > 0 && [`Order Disc${orderDiscType === 'percent' ? ` (${orderDisc}%)` : ''}`, `− ₨ ${fmt(orderDiscAmt)}`, '#dc2626'],
                pointsDiscAmt > 0 && ['Points Redeemed', `− ₨ ${fmt(pointsDiscAmt)}`, '#16a34a'],
                voucherDisc > 0 && [`Voucher (${voucherCode})`, `− ₨ ${fmt(voucherDisc)}`, '#16a34a'],
                taxAmt > 0 && [`GST (${orderTax}%)`, `+ ₨ ${fmt(taxAmt)}`],
              ].filter(Boolean).map(([label, value, color]) => (
                <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="caption" fontWeight={600} color={color || 'text.primary'}>{value}</Typography>
                </Box>
              ))}
              <Divider sx={{ my: 0.75 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="h6" fontWeight={700}>TOTAL</Typography>
                <Typography variant="h6" fontWeight={700} color="primary.main">₨ {fmt(total)}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* ══ RIGHT: Customer + Payment ════════════════════════════════════════ */}
        <Box sx={{ width: '25%', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: 'background.paper' }}>
          <Box sx={{ flex: 1, overflowY: 'auto', p: 1.5, display: 'flex', flexDirection: 'column', gap: 1.5 }}>

            {/* ── Customer ──────────────────────────────────────────────── */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Customer</Typography>
              <Box sx={{ mt: 0.75, position: 'relative' }}>
                {customer ? (
                  <Paper variant="outlined" sx={{ p: 1.25, borderRadius: 2, bgcolor: 'primary.50', borderColor: 'primary.light' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="body2" fontWeight={700}>{customer.name}</Typography>
                        {customer.phone && <Typography variant="caption" color="text.secondary">{customer.phone}</Typography>}
                      </Box>
                      <IconButton size="small" onClick={() => { setCustomer(null); setCustomerSearch(''); setRedeemPoints(0); }} sx={{ p: 0.25 }}>
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                    {/* Khata / credit info */}
                    <Box sx={{ display: 'flex', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
                      {customer.credit_balance > 0 && (
                        <Chip label={`Khata: ₨${fmt(customer.credit_balance)}`} size="small" color="error" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                      )}
                      {customer.loyalty_points > 0 && (
                        <Chip label={`${customer.loyalty_points} pts`} size="small" color="success" variant="outlined" sx={{ fontSize: 10, height: 18 }} />
                      )}
                    </Box>
                    {/* Loyalty redeem */}
                    {customer.loyalty_points > 0 && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75 }}>
                        <Typography variant="caption" sx={{ flex: 1 }}>Redeem (max {maxRedeemable})</Typography>
                        <TextField size="small" type="number" value={redeemPoints} sx={{ width: 60 }}
                          onChange={e => setRedeemPoints(Math.max(0, Math.min(maxRedeemable, +e.target.value)))}
                          inputProps={{ min: 0, max: maxRedeemable, style: { padding: '2px 4px', fontSize: 12 } }} />
                      </Box>
                    )}
                  </Paper>
                ) : (
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    <TextField size="small" fullWidth placeholder="Search customer…"
                      value={customerSearch}
                      onChange={e => { setCustomerSearch(e.target.value); searchCustomers(e.target.value); }}
                      onFocus={() => customerResults.length > 0 && setShowCustResults(true)}
                      onBlur={() => setTimeout(() => setShowCustResults(false), 200)}
                    />
                    <Tooltip title="Add new customer">
                      <IconButton size="small" color="primary" onClick={() => setNewCustDialog(true)}
                        sx={{ border: '1px solid', borderColor: 'primary.main', borderRadius: 1 }}>
                        <PersonAdd fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}

                {/* Customer results dropdown */}
                {!customer && showCustResults && customerResults.length > 0 && (
                  <Paper sx={{ position: 'absolute', zIndex: 20, width: '100%', maxHeight: 200, overflow: 'auto', boxShadow: 4, borderRadius: 1, mt: 0.25 }}>
                    {customerResults.map(c => (
                      <Box key={c.id}
                        onMouseDown={() => { setCustomer(c); setCustomerSearch(c.name); setShowCustResults(false); setCustomerResults([]); }}
                        sx={{ p: 1, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, borderBottom: '1px solid', borderColor: 'divider' }}>
                        <Typography variant="body2" fontWeight={600}>{c.name}</Typography>
                        <Typography variant="caption" color="text.secondary">{c.phone} · {c.loyalty_points || 0} pts</Typography>
                      </Box>
                    ))}
                    <Box onMouseDown={() => setNewCustDialog(true)}
                      sx={{ p: 1, cursor: 'pointer', color: 'primary.main', '&:hover': { bgcolor: 'action.hover' }, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <PersonAdd fontSize="small" />
                      <Typography variant="caption" fontWeight={600}>+ Add new customer</Typography>
                    </Box>
                  </Paper>
                )}
              </Box>
            </Box>

            {/* ── Voucher ───────────────────────────────────────────────── */}
            <Box>
              <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Voucher / Promo</Typography>
              <Box sx={{ display: 'flex', gap: 0.75, mt: 0.75 }}>
                <TextField size="small" placeholder="Enter code…" value={voucherCode}
                  onChange={e => { setVoucherCode(e.target.value.toUpperCase()); setVoucherError(''); }}
                  error={!!voucherError} helperText={voucherError}
                  disabled={!!voucherId} sx={{ flex: 1 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><LocalOffer fontSize="small" /></InputAdornment> }}
                />
                {voucherId
                  ? <Button size="small" color="error" onClick={() => { setVoucherCode(''); setVoucherDisc(0); setVoucherId(null); }}>✕</Button>
                  : <Button size="small" variant="outlined" onClick={applyVoucher} disabled={!voucherCode}>Apply</Button>
                }
              </Box>
              {voucherDisc > 0 && <Typography variant="caption" color="success.main" fontWeight={600}>✓ −₨{voucherDisc} applied</Typography>}
            </Box>

            {/* ── Notes ─────────────────────────────────────────────────── */}
            <TextField size="small" label="Sale Notes" value={saleNotes}
              onChange={e => setSaleNotes(e.target.value)} fullWidth multiline rows={1}
              InputProps={{ startAdornment: <InputAdornment position="start"><Notes fontSize="small" /></InputAdornment> }}
            />

            <Divider />

            {/* ── Payment Methods ───────────────────────────────────────── */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Payment</Typography>
                <Button size="small" startIcon={<Add />} onClick={addPaymentRow} sx={{ fontSize: 11, py: 0.25 }}>Add Method</Button>
              </Box>

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                {payments.map((row, i) => {
                  const pm = PMETHODS.find(m => m.value === row.method);
                  return (
                    <Box key={i} sx={{ display: 'flex', gap: 0.75, alignItems: 'center' }}>
                      <FormControl size="small" sx={{ flex: 1.2 }}>
                        <Select value={row.method} onChange={e => updatePayment(i, 'method', e.target.value)}
                          sx={{ fontSize: 12, bgcolor: pm?.bg || 'background.paper' }}>
                          {PMETHODS.map(m => <MenuItem key={m.value} value={m.value} sx={{ fontSize: 12 }}>{m.label}</MenuItem>)}
                        </Select>
                      </FormControl>
                      <TextField size="small" type="number" value={row.amount}
                        onChange={e => updatePayment(i, 'amount', e.target.value)}
                        onFocus={() => i === payments.length - 1 && !row.amount && autoFillRemaining()}
                        placeholder={i === 0 ? fmt(total) : '0'}
                        sx={{ flex: 1 }}
                        inputProps={{ min: 0, step: 1, style: { padding: '6px 8px', fontSize: 13 } }}
                        InputProps={{ startAdornment: <InputAdornment position="start" sx={{ mr: 0 }}><Typography variant="caption">₨</Typography></InputAdornment> }}
                      />
                      {payments.length > 1 && (
                        <IconButton size="small" onClick={() => removePaymentRow(i)} sx={{ p: 0.25 }}>
                          <Close fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>

              {/* Quick amount chips */}
              {payments.length === 1 && payments[0].method === 'cash' && total > 0 && (
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.75, flexWrap: 'wrap' }}>
                  {[...new Set([
                    Math.ceil(total / 100) * 100,
                    Math.ceil(total / 500) * 500,
                    Math.ceil(total / 1000) * 1000,
                  ].filter(v => v >= total && v !== total))].slice(0, 3).map(v => (
                    <Chip key={v} label={`₨${fmt(v)}`} size="small" clickable
                      onClick={() => updatePayment(0, 'amount', v)}
                      variant="outlined" color="success" sx={{ fontSize: 10, height: 20 }} />
                  ))}
                  <Chip label="Exact" size="small" clickable
                    onClick={() => updatePayment(0, 'amount', total.toFixed(0))}
                    variant="outlined" color="primary" sx={{ fontSize: 10, height: 20 }} />
                </Box>
              )}

              {/* Change / Due */}
              {totalPaid > 0 && (
                <Box sx={{ mt: 0.75, p: 1, borderRadius: 1.5, bgcolor: change > 0 ? '#f0fdf4' : due > 0 ? '#fef2f2' : '#f0fdf4', border: '1px solid', borderColor: change > 0 ? 'success.light' : due > 0 ? 'error.light' : 'success.light' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" fontWeight={600}>Collected</Typography>
                    <Typography variant="body2" fontWeight={700}>₨ {fmt(totalPaid)}</Typography>
                  </Box>
                  {change > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                      <Typography variant="body2" color="success.main" fontWeight={700}>Change Return</Typography>
                      <Typography variant="h6" color="success.main" fontWeight={700}>₨ {fmt(change)}</Typography>
                    </Box>
                  )}
                  {due > 0 && (
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.25 }}>
                      <Typography variant="body2" color="error.main" fontWeight={700}>Balance Due</Typography>
                      <Typography variant="h6" color="error.main" fontWeight={700}>₨ {fmt(due)}</Typography>
                    </Box>
                  )}
                </Box>
              )}
            </Box>

          </Box>{/* end scroll area */}

          {/* ── Bottom action buttons (sticky) ───────────────────────── */}
          <Box sx={{ p: 1.5, borderTop: '2px solid', borderColor: 'divider', bgcolor: 'background.paper', flexShrink: 0 }}>
            <Button
              fullWidth variant="contained" size="large"
              onClick={completeSale} disabled={loading || !cart.length}
              startIcon={loading ? <CircularProgress size={18} color="inherit" /> : <Check />}
              sx={{ py: 1.5, fontSize: 15, fontWeight: 700, borderRadius: 2, mb: 0.75, background: cart.length ? 'linear-gradient(135deg, #6366f1, #4f46e5)' : undefined }}>
              {loading ? 'Processing…' : `Complete Sale  ₨${fmt(total)}`}
            </Button>
            <Box sx={{ display: 'flex', gap: 0.75 }}>
              <Button fullWidth variant="outlined" size="small" startIcon={<Pause />}
                onClick={holdSale} disabled={!cart.length} color="warning">Hold</Button>
              <Button fullWidth variant="outlined" size="small" color="error"
                onClick={clearAll} disabled={!cart.length}>Cancel</Button>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Hidden print */}
      {completedSale && (
        <Box sx={{ display: 'none' }}>
          <InvoicePrint ref={printRef} sale={completedSale} format={invoiceFormat} tenant={tenant} />
        </Box>
      )}

      {/* ══ DIALOGS ══════════════════════════════════════════════════════════ */}

      {/* Variant Selector — one click per variant row */}
      <Dialog open={!!variantDialog} onClose={() => setVariantDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
          <Avatar sx={{ bgcolor: variantDialog ? productColor(variantDialog.product.name) : 'grey.400', width: 32, height: 32, fontSize: 14 }}>
            {variantDialog?.product.name?.[0]}
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={700}>{variantDialog?.product.name}</Typography>
            <Typography variant="caption" color="text.secondary">Select a variant to add to cart</Typography>
          </Box>
          {variantLoading && <CircularProgress size={16} />}
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {variantDialog?.product.is_medicine && (
            <Alert severity="warning" sx={{ mb: 1.5, py: 0.5, fontSize: 12 }}>⚕ Check expiry date before dispensing</Alert>
          )}

          {variantDialog?.variants.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 3 }}>
              <Alert severity="info">No variants configured for this product.</Alert>
              <Button sx={{ mt: 2 }} variant="outlined" onClick={() => {
                addToCartItem({
                  product_id: variantDialog.product.id, product_name: variantDialog.product.name,
                  sku: variantDialog.product.sku, unit_price: variantDialog.product.sale_price,
                  quantity: 1, discount_amount: 0, stock: variantDialog.product.stock,
                  variant_id: null, variant_label: null, is_medicine: variantDialog.product.is_medicine,
                });
                setVariantDialog(null);
              }}>Add at Default Price</Button>
            </Box>
          ) : (
            /* Group variants by attribute_name */
            Object.entries(variantGroups).map(([attrName, variants]) => (
              <Box key={attrName} sx={{ mb: 2 }}>
                <Typography variant="caption" fontWeight={700} color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.75, display: 'block' }}>
                  {attrName}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {variants.map(v => {
                    const outOfStock = variantStock(v) <= 0;
                    const price = variantPrice(v) || variantDialog.product.sale_price;
                    return (
                      <Paper key={v.id} variant="outlined"
                        onClick={() => !outOfStock && addFromVariant(v)}
                        sx={{
                          px: 1.5, py: 1, cursor: outOfStock ? 'not-allowed' : 'pointer',
                          borderRadius: 2, opacity: outOfStock ? 0.5 : 1,
                          borderColor: 'divider', minWidth: 80, textAlign: 'center',
                          transition: 'all .15s',
                          '&:hover': outOfStock ? {} : {
                            borderColor: 'primary.main', bgcolor: 'primary.50',
                            transform: 'translateY(-1px)', boxShadow: 2,
                          },
                        }}>
                        <Typography variant="body2" fontWeight={700}>
                          {v.attribute_value || variantLabel(v)}
                        </Typography>
                        {price > 0 && (
                          <Typography variant="caption" color="primary.main" fontWeight={600}>
                            ₨{fmt(price)}
                          </Typography>
                        )}
                        <Typography variant="caption" display="block"
                          color={outOfStock ? 'error.main' : variantStock(v) <= 5 ? 'warning.main' : 'success.main'}>
                          {outOfStock ? 'OUT' : `${variantStock(v)} left`}
                        </Typography>
                      </Paper>
                    );
                  })}
                </Box>
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVariantDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Custom Item */}
      <Dialog open={customDialog} onClose={() => setCustomDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Custom Item</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField autoFocus label="Item Name / Description" value={customName}
            onChange={e => setCustomName(e.target.value)} fullWidth required
            placeholder="e.g. Labor charge, Packaging, Custom work" />
          <Box sx={{ display: 'flex', gap: 1 }}>
            <TextField label="Price (₨)" type="number" value={customPrice}
              onChange={e => setCustomPrice(e.target.value)} sx={{ flex: 1 }} required
              inputProps={{ min: 0, step: 0.01 }} />
            <TextField label="Qty" type="number" value={customQty}
              onChange={e => setCustomQty(e.target.value)} sx={{ width: 80 }}
              inputProps={{ min: 0.001, step: 0.01 }} />
          </Box>
          {customName && customPrice && (
            <Alert severity="info" sx={{ py: 0.5 }}>Total: ₨ {fmt((+customPrice || 0) * (+customQty || 1))}</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCustomDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={addCustomItem} disabled={!customName || !customPrice} startIcon={<AddBox />}>Add to Cart</Button>
        </DialogActions>
      </Dialog>

      {/* New Customer */}
      <Dialog open={newCustDialog} onClose={() => setNewCustDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><PersonAdd color="primary" /> Add Customer</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField autoFocus label="Full Name *" value={newCustName}
            onChange={e => setNewCustName(e.target.value)} fullWidth
            onKeyDown={e => e.key === 'Enter' && createCustomer()} />
          <TextField label="Phone (optional)" value={newCustPhone}
            onChange={e => setNewCustPhone(e.target.value)} fullWidth placeholder="03xx-xxxxxxx" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewCustDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={createCustomer} disabled={!newCustName.trim() || newCustLoading} startIcon={<PersonAdd />}>
            {newCustLoading ? 'Saving…' : 'Add & Select'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Item Note */}
      <Dialog open={noteIdx !== null} onClose={() => setNoteIdx(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Item Note — {noteIdx !== null ? cart[noteIdx]?.product_name : ''}</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField autoFocus multiline rows={3} fullWidth label="Note" value={noteVal}
            onChange={e => setNoteVal(e.target.value)} placeholder="e.g. warranty, color preference, alteration…" />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNoteIdx(null)}>Cancel</Button>
          <Button variant="contained" onClick={saveNote}>Save Note</Button>
        </DialogActions>
      </Dialog>

      {/* Held Sales */}
      <Dialog open={heldDialog} onClose={() => setHeldDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Held Sales ({heldSales.length})</DialogTitle>
        <DialogContent>
          {heldSales.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>No held sales</Typography>
          ) : (
            <List disablePadding>
              {heldSales.map(h => (
                <ListItem key={h.id} divider
                  secondaryAction={
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <Button size="small" variant="contained" onClick={() => retrieveHeld(h)}>Restore</Button>
                      <IconButton size="small" color="error" onClick={() => deleteHeld(h.id)}><Delete fontSize="small" /></IconButton>
                    </Box>
                  }>
                  <ListItemText
                    primary={`${h.cart.length} items — ₨ ${fmt(h.cart.reduce((s, i) => s + i.unit_price * i.quantity, 0))}`}
                    secondary={`${h.notes || h.cart.map(i => i.product_name).join(', ').slice(0, 50)} · ${new Date(h.heldAt).toLocaleTimeString('en-PK')}`}
                    sx={{ pr: 14 }} />
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setHeldDialog(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Quick Expense */}
      <Dialog open={expDialog} onClose={() => setExpDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Quick Expense</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          <TextField select label="Category" value={expCat} onChange={e => setExpCat(e.target.value)} fullWidth required>
            {expCats.map(c => <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>)}
            {expCats.length === 0 && <MenuItem value="" disabled>No categories</MenuItem>}
          </TextField>
          <TextField label="Amount (₨)" type="number" value={expAmount}
            onChange={e => setExpAmount(e.target.value)} fullWidth required inputProps={{ min: 0 }} />
          <TextField label="Description" value={expNote} onChange={e => setExpNote(e.target.value)} fullWidth />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExpDialog(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={submitExpense}
            disabled={!expAmount || !expCat || expLoading} startIcon={<AttachMoney />}>
            {expLoading ? 'Saving…' : 'Record'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Post-sale */}
      <Dialog open={!!completedSale} onClose={() => setCompletedSale(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ textAlign: 'center', pb: 0 }}>
          <Box sx={{ width: 56, height: 56, bgcolor: 'success.main', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1 }}>
            <Check sx={{ color: 'white', fontSize: 32 }} />
          </Box>
          <Typography variant="h6" fontWeight={700}>Sale Complete!</Typography>
          <Typography variant="caption" color="text.secondary">{completedSale?.invoiceNumber}</Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 1 }}>
          <Typography variant="h4" fontWeight={700} color="primary" gutterBottom>₨ {fmt(completedSale?.total)}</Typography>
          {completedSale?.change > 0 && (
            <Alert severity="success" sx={{ mb: 1 }}>Return to customer: ₨ {fmt(completedSale.change)}</Alert>
          )}
          {completedSale?.customer?.phone && (
            <Button fullWidth variant="outlined" startIcon={<WhatsApp />} sx={{ mb: 1, borderColor: '#25D366', color: '#25D366', '&:hover': { bgcolor: '#25D36610' } }}
              onClick={async () => {
                try {
                  await api.post('/whatsapp/send', {
                    phone: completedSale.customer.phone,
                    message: `Dear ${completedSale.customer.name}, your invoice ${completedSale.invoiceNumber} for ₨${fmt(completedSale.total)} is complete. Thank you! 🛒`,
                  });
                  dispatch(showSnackbar({ message: 'WhatsApp receipt sent!', severity: 'success' }));
                } catch { dispatch(showSnackbar({ message: 'WhatsApp failed', severity: 'error' })); }
              }}>
              Send WA Receipt
            </Button>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 1, pb: 2, flexWrap: 'wrap' }}>
          <Button variant="outlined" startIcon={<Print />} onClick={handlePrint}>Reprint</Button>
          <Button variant="outlined" color="error" startIcon={<PictureAsPdf />}
            onClick={async () => {
              const auth = JSON.parse(localStorage.getItem('pos_auth') || '{}');
              const res = await fetch(
                `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/export/pdf/invoice/${completedSale.saleId}`,
                { headers: { Authorization: `Bearer ${auth.accessToken}` } }
              );
              if (!res.ok) { alert('PDF failed'); return; }
              window.open(URL.createObjectURL(await res.blob()), '_blank');
            }}>
            Invoice PDF
          </Button>
          <Button variant="contained" startIcon={<AddShoppingCart />} onClick={() => setCompletedSale(null)}>New Sale</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
}
